import { Box, Stack, SxProps, Tab, Tabs, Theme } from '@mui/material';
import { ReactNode } from 'react';
import { mergeSx } from '@/utils/cssStyles';

const PILL_CLASS = 'PSubTabs-pill';

/** Vertical padding of a sub-tab pill. Exported so content beside a `PSubTabs` row can align with it. */
export const SUB_TAB_PILL_PADDING_Y = 1;

export type PSubTabItem<T extends string> = {
  value: T;
  label: ReactNode;
  /** Rendered inside the pill, before the label. */
  icon?: ReactNode;
  /** Rendered inside the pill, after the label — e.g. a validation warning. */
  adornment?: ReactNode;
  /** Secondary content placed below the pill and outside it. */
  caption?: ReactNode;
  disabled?: boolean;
};

type Props<T extends string> = {
  value: T;
  onChange: (value: T) => void;
  tabs: PSubTabItem<T>[];
  sx?: SxProps<Theme>;
};

/** Second-level tabs: the sub-navigation inside a page-level tab. */
export const PSubTabs = <T extends string>({ value, onChange, tabs, sx }: Props<T>) => (
  <Tabs
    value={value}
    onChange={(_, next: T) => onChange(next)}
    variant="scrollable"
    scrollButtons="auto"
    allowScrollButtonsMobile
    TabIndicatorProps={{ sx: { display: 'none' } }}
    sx={mergeSx({ minHeight: 0 }, sx)}
  >
    {tabs.map((tab) => (
      <Tab
        key={tab.value}
        value={tab.value}
        disabled={tab.disabled}
        disableRipple
        label={
          <Stack alignItems="flex-start">
            <Box
              className={PILL_CLASS}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                px: 2,
                py: SUB_TAB_PILL_PADDING_Y,
                borderRadius: 1,
              }}
            >
              {tab.icon}
              {tab.label}
              {tab.adornment}
            </Box>
            {tab.caption ? <Box sx={{ px: 2, pt: 0.5 }}>{tab.caption}</Box> : null}
          </Stack>
        }
        sx={{
          p: 0,
          minWidth: 0,
          minHeight: 0,
          alignItems: 'flex-start',
          '&:not(:last-of-type)': { mr: 1 },
          '&.Mui-selected': { color: 'text.primary' },
          [`&.Mui-selected .${PILL_CLASS}`]: { bgcolor: 'action.selected' },
          [`&:hover:not(.Mui-selected):not(.Mui-disabled) .${PILL_CLASS}`]: { bgcolor: 'action.hover' },
        }}
      />
    ))}
  </Tabs>
);
